import * as React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    TablePagination,
    TableFooter,
    Chip // Import Chip from MUI
} from '@mui/material';
import { ThemeMode } from 'config';
import { alpha, useTheme } from '@mui/material/styles';

interface TableProps {
    data?: Array<Record<string, any>>; // Ensure data is optional and defaults to an empty array
    hasCheckboxSelection: boolean;
}

export const AgenciesTable: React.FC<TableProps> = ({ data = [], hasCheckboxSelection = false }) => {
    const [selectedRows, setSelectedRows] = React.useState<number[]>([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const theme = useTheme();

    // Ensure data is always an array and add a hardcoded status
    const tableData = Array.isArray(data)
        ? data.map((row) => ({ ...row, status: 'Active', statuscolor: 'green' })) // Adding hardcoded status and color
        : [];

    if (tableData.length === 0) {
        return <Box className="ml-6 mb-4">No data available</Box>;
    }

    // Define a mapping between data keys and their display names
    const keyDisplayMap = {
        name: 'Agency Name',
        emailAddress: 'Email',
        registrationNumber: 'Registration Number',
        status: 'Status' // Include status in the display map
    };

    // Define the custom order for keys
    const orderedKeys = ['name', 'emailAddress', 'registrationNumber', 'status'];

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelecteds = tableData.map((_, index) => index);
            setSelectedRows(newSelecteds);
            return;
        }
        setSelectedRows([]);
    };

    const handleClick = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const selectedIndex = selectedRows.indexOf(index);
        let newSelected: number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, index);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1));
        }

        setSelectedRows(newSelected);
    };

    const isSelected = (index: number) => selectedRows.indexOf(index) !== -1;

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                        {hasCheckboxSelection && (
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedRows.length > 0 && selectedRows.length < tableData.length}
                                    checked={tableData.length > 0 && selectedRows.length === tableData.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                        )}
                        {orderedKeys.map((key) => (
                            <TableCell key={key} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#000 !important' }}>
                                {keyDisplayMap[key] || key.charAt(0).toUpperCase() + key.slice(1)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => {
                        const isItemSelected = isSelected(rowIndex);
                        return (
                            <TableRow key={rowIndex} hover role="checkbox" aria-checked={isItemSelected} selected={isItemSelected}>
                                {hasCheckboxSelection && (
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={isItemSelected} onChange={(event) => handleClick(event, rowIndex)} />
                                    </TableCell>
                                )}
                                {orderedKeys.map((key) => (
                                    <TableCell
                                        key={key}
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {key === 'status' ? (
                                            <Chip
                                                label={row.status} // Display the status
                                                size="small"
                                                sx={{
                                                    bgcolor:
                                                        theme.palette.mode === ThemeMode.DARK
                                                            ? 'dark.main'
                                                            : alpha(theme.palette.success.light, 0.6),
                                                    color: 'success.dark'
                                                }}
                                            />
                                        ) : typeof row[key] === 'object' && row[key] !== null && 'name' in row[key] ? (
                                            (row[key] as { name: string }).name
                                        ) : typeof row[key] === 'string' || typeof row[key] === 'number' ? (
                                            row[key]
                                        ) : React.isValidElement(row[key]) ? (
                                            row[key]
                                        ) : (
                                            JSON.stringify(row[key])
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 15, 20]}
                            count={tableData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
};
